import React, { useEffect, useState } from 'react';
import { Modal, Box, Skeleton } from '@mui/material';
import { gql, useQuery } from '@apollo/client';
import { startOfDay, formatISO, endOfDay } from 'date-fns';
import {
  GetVisitDetailsQuery,
  CommandIndexProvidersQuery,
} from '~/graphql/__generated__/graphql';
import { GET_VISIT_DETAILS_QUERY } from '../command/VisitPopup';
import { COMMAND_INDEX_PROVIDERS_QUERY } from '~/queries/forms';
import PatientProviderFirstStep from './PatientProviderFirstStep';
import ProviderPatientFirstStep from './ProviderPatientFirstStep';
import OverrideVisitConfigForm from './OverrideVisitConfigForm';
import { OverrideVisitPatient, CreateOverrideVisitProvider } from './overrideVisitTypes';
import { useOrganizationalUnitSegmentationEnabled } from '~/hooks/permissions';
export type { CreateOverrideVisitProvider } from './overrideVisitTypes';

type FlowStep = 'select-first' | 'configure';

interface CreateOverrideVisitWrapperProps {
  handleClose: () => void;
  calendarDateTime: Date;
  selectedProvider?: CreateOverrideVisitProvider;
  onSuccess: (visitId: number) => void;
  visitId?: number | null;
  prefilledPatient?: OverrideVisitPatient;
}



const CreateOverrideVisitWrapper: React.FC<CreateOverrideVisitWrapperProps> = ({
  handleClose, selectedProvider, calendarDateTime, onSuccess, visitId, prefilledPatient
}) => {
  const isEditModeFlag = !!visitId;
  const organizationalUnitSegmentationEnabled = useOrganizationalUnitSegmentationEnabled();
  
  const isPatientStartFlow = !!prefilledPatient && !selectedProvider;
  const isProviderStartFlow = !!selectedProvider && !prefilledPatient;
  
  const [flowStep, setFlowStep] = useState<FlowStep>(() => {
    if (isEditModeFlag) return 'configure';
    return 'select-first';
  });
  
  const [selectedPatientFromFlow, setSelectedPatientFromFlow] = useState<{ id: number; title: string } | null>(null);
  const [selectedProviderFromFlow, setSelectedProviderFromFlow] = useState<CreateOverrideVisitProvider | null>(null);

  const { data: providerData, loading: providersLoading, refetch: refetchProviders } = useQuery<CommandIndexProvidersQuery>(COMMAND_INDEX_PROVIDERS_QUERY, {
    variables: {
      includeShifts: true,
      start: formatISO(startOfDay(calendarDateTime)),
      end: formatISO(endOfDay(calendarDateTime))
    }
  });

  const { data: visitData, loading: visitLoading } = useQuery<GetVisitDetailsQuery>(GET_VISIT_DETAILS_QUERY, {
    variables: { visitId },
    skip: !isEditModeFlag
  });

  useEffect(() => {
    refetchProviders();
  }, [refetchProviders]);

  const isLoading = providersLoading || visitLoading;

  const effectiveProvider = selectedProviderFromFlow || selectedProvider;
  const effectivePatient = selectedPatientFromFlow 
    ? { id: selectedPatientFromFlow.id, firstName: selectedPatientFromFlow.title.split(' ')[0], lastName: selectedPatientFromFlow.title.split(' ').slice(1).join(' ') }
    : prefilledPatient;

  const handleProviderSelectedFromPatientFlow = (provider: CreateOverrideVisitProvider) => {
    setSelectedProviderFromFlow(provider);
    setFlowStep('configure');
  };

  const handlePatientSelectedFromProviderFlow = (patient: { id: number; title: string }) => {
    setSelectedPatientFromFlow(patient);
    setFlowStep('configure');
  };

  const renderContent = () => {
    if (isLoading || organizationalUnitSegmentationEnabled === undefined) {
      return (
        <Box sx={{ p: 2, minWidth: 550 }}>
          <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" width="100%" height={100} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" width="100%" height={80} />
        </Box>
      );
    }

    if (flowStep === 'select-first') {
      if (isPatientStartFlow && !organizationalUnitSegmentationEnabled) {
        return (
          <PatientProviderFirstStep
            providers={providerData?.commandIndexProviders?.providers || []}
            loading={providersLoading}
            calendarDateTime={calendarDateTime}
            onProviderSelected={handleProviderSelectedFromPatientFlow}
            onCancel={handleClose}
          />
        );
      }
      
      if (isProviderStartFlow) {
        return (
          <ProviderPatientFirstStep
            provider={selectedProvider!}
            calendarDateTime={calendarDateTime}
            onPatientSelected={handlePatientSelectedFromProviderFlow}
            onCancel={handleClose}
          />
        );
      }
    }

    return (
      <OverrideVisitConfigForm
        isEditMode={isEditModeFlag}
        providerData={providerData!}
        visitData={visitData!}
        handleClose={handleClose}
        selectedProvider={effectiveProvider}
        calendarDateTime={calendarDateTime}
        onSuccess={onSuccess}
        visitId={visitId}
        prefilledPatient={effectivePatient}
      />
    );
  };

  return (
    <Modal
      open={true}
      onClose={handleClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        minWidth: 400,
        maxWidth: '90vw',
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        wordWrap: 'break-word',
        overflowWrap: 'break-word'
      }}>
        {renderContent()}
      </Box>
    </Modal>
  );
};

export default CreateOverrideVisitWrapper;

gql`
  mutation GenerateVisitsWarnings(
    $patientId: Int!,
    $serviceLineId: Int!,
    $visitTypeId: Int!,
    $resources: [VisitResourceWithRequirement]!,
    $reschedulingVisitId: Int
  )
  @permission(requires: createOverrideVisits, check: [userInServiceLine])
  {
    generateVisitWarnings(
      patientId: $patientId,
      serviceLineId: $serviceLineId,
      visitTypeId: $visitTypeId,
      resources: $resources,
      reschedulingVisitId: $reschedulingVisitId
    ) {
      visitWarningLogId
      visitWarnings {
        visitId
        severity
        resourceRequirementProviderTypes {
          expectedProviderTypes
          actualProviderIds
          extraProviderIds
        }
        teamMismatch
      }
      visitResourceWarnings {
        visitId
        providerId
        severity
        shiftMissing
        duration {
          expectedDurationInMinutes
          actualDurationInMinutes
        }
        previousVisitDriveTimeOverlap
        nextVisitDriveTimeOverlap
        startOfShiftDriveTimeOverlap
        endOfShiftDriveTimeOverlap
        gapCreatedBeforeVisit
        gapCreatedAfterVisit
        excessiveDriveTimeBeforeVisit
        excessiveDriveTimeAfterVisit
        previousBlocker {
          type
          label
        }
        nextBlocker {
          type
          label
        }
      }
    }
  }
`;

export const GET_PATIENT_INSTRUCTION_QUERY = gql`
  query GetPatientInstructions($patientId: Int!)
  @permission(requires: scheduleVisits) {
    patientSingle(where: { id: { eq: $patientId } }) {
      id
      addressTimezone
      addressState
      serviceInstructions
      episodes {
        id
        active
        serviceLineId
        organizationalUnitId
      }
      careTeamProviders {
        id
        providerId
        providerTypeId
        provider {
          providerTypeId
        }
      }
    }
    organizationalUnit(orderBy: { name: { direction: asc, priority: 1 } }) {
      id
      name
      active
    }
  }
`;
